import type { ReactElement } from "react";

import type { IconName } from "../../design-system/icons";
import { useTranslation } from "../../i18n";
import { Button } from "../atoms/Button";
import { Icon } from "../atoms/Icon";
import styles from "./Toast.module.css";

/** The three tones a temporary notice can carry (annex §11.3). */
export type ToastTone = "info" | "success" | "danger";

export interface ToastProps {
  readonly tone: ToastTone;
  readonly message: string;
  readonly onDismiss: () => void;
  /** One optional action, drawn as a text button before the dismiss control. */
  readonly action?: { readonly label: string; readonly onSelect: () => void } | undefined;
}

/** The mockup's toast glyph per tone; the tone is also said by the region, never by the glyph alone. */
const TONE_GLYPHS: Readonly<Record<ToastTone, IconName>> = {
  success: "check-circle",
  danger: "alert-circle",
  info: "info",
};

/** Whether a tone interrupts: only a failure goes to the assertive region. */
export function interrupts(tone: ToastTone): boolean {
  return tone === "danger";
}

/**
 * One temporary notice, and the reason `alert` and `confirm` are banned.
 *
 * How it is announced depends on its tone (verifier m6). `info` and `success` carry no role: the
 * provider inserts them into a polite live region mounted from the start, because polite content is
 * announced reliably only when added to a region that already exists. `danger` carries
 * `role="alert"`, which screen readers announce at the moment the element is inserted, and it
 * interrupts. Interrupting for a success message is how people learn to ignore a screen reader;
 * staying polite about a failure is how they miss it.
 *
 * The dismiss control is a real `<button>` whose accessible name is visible
 * text placed off-screen, not an `aria-label`: the name then comes from the
 * dictionary through the same path as every other string (F-UIX-015).
 */
export function Toast({ tone, message, onDismiss, action }: ToastProps): ReactElement {
  const { t } = useTranslation();
  const glyph = TONE_GLYPHS[tone];

  return (
    <div className={[styles.toast, styles[tone]].join(" ")} role={interrupts(tone) ? "alert" : undefined}>
      <span className={styles.glyph} data-glyph={glyph}>
        <Icon name={glyph} />
      </span>
      <span className={styles.message}>{message}</span>
      {action === undefined ? null : (
        <Button variant="ghost" size="sm" onClick={action.onSelect}>
          {action.label}
        </Button>
      )}
      <Button variant="ghost" size="sm" iconOnly onClick={onDismiss}>
        <Icon name="close" size="sm" />
        <span className={styles.dismissLabel}>{t("common.action.dismiss")}</span>
      </Button>
    </div>
  );
}
